import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link, PageProps } from 'gatsby'
import clsx from 'clsx'
import {
  Button,
  Card,
  Error,
  Pagination,
  Search,
  SEO,
  Space,
  Title,
} from 'components/common'
import { Rate, RatesTable } from 'components/rates'
import { usePagination } from 'lib/hooks'
import {
  RatesQuery,
  RatesQueryResult,
  RatesQueryVariables,
  RateStatus,
  useRatesQuery,
} from 'lib/graphql/storeadmin'
import { datetime } from 'lib/str-formatter/datetime'

export const Head = () => <SEO title="料金テーブル管理" />

const IndexPage = ({ location }: PageProps<Queries.Query>): JSX.Element => {
  const [searchFiled, setSearchFiled] = useState<string>(null)
  const [warning, setWarning] = useState<string>(null)
  const [queryVariables, setQueryVariables] = useState<RatesQueryVariables>({
    status: null,
    title_Contains: null,
  })
  const { data, loading, error, goto, refetch, page, size, paginate } =
    usePagination<RatesQuery, RatesQueryVariables, RatesQueryResult>({
      initialSize: 10,
      queryOptions: { variables: queryVariables },
      queryHooks: useRatesQuery,
    })

  useEffect(() => {
    const checkErrorNotFound: boolean = error?.message?.toString()?.length <= 0
    if (checkErrorNotFound) {
      refetch(queryVariables)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryVariables]) // deps に refetch が含まれていると、永遠に refetch し続ける

  const dataLength = data?.rates?.totalCount
  const dataSource = useMemo<Array<Rate>>(() => {
    const tableData = data?.rates?.edges?.reduce(
      (result: Array<Rate>, { rate }) => {
        const { id, title, variants, status, createdAt, updatedAt } = rate
        result.push({
          key: id,
          title: title.jaJp,
          status: status,
          numberOfItems: variants.edges.length,
          createdAt: datetime(createdAt),
          updatedAt: datetime(updatedAt),
        })
        return result
      },
      []
    )
    return tableData
  }, [data])

  const handleFiltering = useCallback(
    (key: string) => {
      goto(1)
      setWarning(null)
      setSearchFiled(null)
      switch (key) {
        case RateStatus.Active:
          return setQueryVariables({ status: RateStatus.Active })
        case RateStatus.Inactive:
          return setQueryVariables({ status: RateStatus.Inactive })
        case RateStatus.Archived:
          return setQueryVariables({ status: RateStatus.Archived })
        default:
          return setQueryVariables({ status: null })
      }
    }, // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  )

  const handleSearch = () => {
    if (!searchFiled) {
      setWarning('絞り込みたい内容を入力してください')
      return
    }
    setWarning(null)
    setQueryVariables({
      ...queryVariables,
      title_Contains: searchFiled?.replace(/\s/g, ''),
    })
  }
  const handleSearchClear = () => {
    if (searchFiled !== null) {
      goto(1)
      setSearchFiled(null)
      setQueryVariables({
        ...queryVariables,
        title_Contains: null,
      })
    }
  }

  if (error) {
    return Error.render(error)
  }

  return (
    <>
      <div className={clsx('flex', 'justify-between', 'items-center')}>
        <div>
          <Title level={1}>料金テーブル管理</Title>
        </div>
        <div className={clsx('flex', 'justify-end')}>
          <Button type="primary">
            <Link to="./new">料金テーブルを追加する</Link>
          </Button>
        </div>
      </div>

      <Card
        tabList={[
          { key: 'all', tab: 'すべて' },
          { key: RateStatus.Active, tab: 'アクティブ' },
          { key: RateStatus.Inactive, tab: '非アクティブ' },
          { key: RateStatus.Archived, tab: 'アーカイブ済み' },
        ]}
        shadow
        onTabChange={(key: string) => handleFiltering(key)}
      >
        <div className={clsx('section')}>
          {warning && <div className="ml-2 text-red-600">{warning}</div>}
          <Space direction="horizontal">
            <Search
              autoComplete="off"
              placeholder="料金区分を絞り込む"
              value={searchFiled}
              onChange={(e) => setSearchFiled(e.target.value)}
              onSearch={handleSearch}
            />
            <Button onClick={handleSearch}>絞り込む</Button>
            <Button onClick={handleSearchClear}>クリア</Button>
            {/* TODO: Phase 1 では非表示 */}
            {/*<Select defaultValue="latestUpdate" style={{ width: 200 }}>*/}
            {/*  <Select.Option value="latestUpdate">*/}
            {/*    最終更新（最新順）*/}
            {/*  </Select.Option>*/}
            {/*</Select>*/}
          </Space>
        </div>

        <Space direction="vertical" size="large">
          <RatesTable dataSource={dataSource} loading={loading} />
          <Pagination
            current={page}
            pageSize={size}
            total={dataLength}
            onChange={paginate}
            showSizeChanger
          />
        </Space>
      </Card>
    </>
  )
}

export default IndexPage
